import React, { useEffect, useState, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import addToCartRequest, { AddToCartProduct } from "~/ProductDetails/Pricing/AddToCart/request";
import { Accessory } from "~/common/Store/models/ProductListing";
import Notification from "~/common/Notification";
import SuccessMessage from "~/productListing/ProductBox/SuccessMessage";
import CustomizeButton from "./CustomizeButton";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import QuantityDropdown from "./CarouselQuantityDropdown";
import { getValidationError } from "../Pricing/AddToCart/validations";
import getMinBuyForQuantityDropdown from "../Pricing/AddToCart/getMinBuyForQuantityDropdown";
import NotifyMeButton from "./NotifyMeButton";
import { ProductReplacement } from "../ProductDetailPageResult";
import { AddToCartModalNames } from "~/common/AddToCartModal";
import ViewReplacementButton from "./ViewReplacementButton";
import ViewDetailsButton from "./ViewDetailsButton";
import useAddToCartModals from "~/common/AddToCartModal/useAddToCartModals";
import AccessoriesModal from "~/common/AddToCartModal/AccessoriesModal";
import fetchAccessories from "./fetchAccessories";
import { getCarouselProps } from "~/analytics/AddToCart/carouselAddToCartAnalytics";
import { addToCartAnalytics } from "~/analytics/AddToCart/addToCartAnalytics";
import { onClickCtrTrack } from "~/analytics/ClickThroughRateTracking/clickThroughRateHelpers";
import { cls } from "@wss/cls";
export interface CarouselAddToCartProduct {
  customizationRequired: boolean;
  customizationUrl: string;
  description: string;
  feedIdentifier: string;
  grouping: boolean;
  hasAccessories: boolean;
  imagePath: string;
  isCustomizable: boolean;
  itemNumber: string;
  link: string;
  minimumBuy: number;
  mustBuy: number;
  outOfStock: boolean;
  price: number;
  productReplacement: ProductReplacement;
  isReplacementEligible: boolean;
}
interface Props {
  buttonClasses?: string;
  useSingleItemStyle: boolean;
  formId: string;
  product: CarouselAddToCartProduct;
  userEmail?: string;
  addCarouselItemToCart?: (itemNumber: string, quantity: string, accessories?: string) => void;
  extraQuantityContainerClassName?: string;
  containsQuantityDropdown?: boolean;
  setIsQuantityShown?: (value: boolean) => void;
  carouselName?: string;
  originalLinkingItemNumber?: string;
  isMiniCarousel?: boolean;
}
const CarouselAddToCart = ({
  buttonClasses,
  useSingleItemStyle,
  formId,
  product,
  userEmail,
  addCarouselItemToCart,
  containsQuantityDropdown,
  setIsQuantityShown,
  extraQuantityContainerClassName = "",
  carouselName,
  originalLinkingItemNumber,
  isMiniCarousel = false
}: Props) => {
  const {
    minimumBuy,
    mustBuy,
    hasAccessories
  } = product;
  const isHandlingAddToCartCall = addCarouselItemToCart === undefined || addCarouselItemToCart === null;
  const inputRef = useRef<HTMLInputElement>(null);
  const formClassName = useSingleItemStyle ? "product_form" : "form product_form";
  const updatedStartBuy = getMinBuyForQuantityDropdown({
    minBuy: minimumBuy,
    mustBuy
  });
  const [loadingAccessories, setLoadingAccessories] = useState(false);
  const [accessories, setAccessories] = useState<readonly Accessory[]>([]);
  const [forceShowingCustomQuantity, setShowQuantityInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const showQuantityDropdown = (minimumBuy > 1 || mustBuy > 1) && !forceShowingCustomQuantity;
  const [quantity, setQuantity] = useState(updatedStartBuy.toString());
  useEffect(() => {
    if (errorMessage && isHandlingAddToCartCall) Notification.closeAll();
  }, [errorMessage, isHandlingAddToCartCall]);
  const qtyValue = (value: string) => Number(value) || 0;
  const handleQuantityChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const inputValue = event.target.value;
    let quantityValue = inputValue;
    if (inputValue === "other") {
      if (!forceShowingCustomQuantity) {
        setShowQuantityInput(true);
        quantityValue = updatedStartBuy.toString();
      }
    }
    if (quantityValue !== "" && Number.isNaN(quantityValue)) {
      quantityValue = updatedStartBuy.toString();
    }
    setErrorMessage("");
    setQuantity(quantityValue);
  };
  interface AddToCartArgs {
    openModals?: (modals: AddToCartModalNames[]) => void;
    accessoriesList?: string[];
  }
  const loadAccessories = async () => {
    if (loadingAccessories || !hasAccessories) {
      return;
    }
    setLoadingAccessories(true);
    const data = await fetchAccessories(product.itemNumber);
    setAccessories(data);
  };
  const AddToCart = async ({
    openModals,
    accessoriesList
  }: AddToCartArgs) => {
    const addToCartProduct: AddToCartProduct = {
      ...product
    };
    if (openModals) {
      const modalsToOpen: AddToCartModalNames[] = [];
      if (hasAccessories && !accessoriesList) {
        loadAccessories();
        modalsToOpen.push("accessories");
      }
      if (modalsToOpen.length > 0) {
        openModals(modalsToOpen);
        return;
      }
    }
    const validationError = getValidationError({
      product: {
        ...product,
        grouping: false,
        groupingAttributes: {
          isVirtual: false,
          groupMembers: []
        },
        startBuy: updatedStartBuy,
        accessories: [] // NOTE: Not validating accessories here as they have been validated in the modal and we can't change them here
      },
      quantity: Number(quantity)
    });
    setErrorMessage(validationError?.message ?? "");
    if (validationError && validationError.message?.length > 0) {
      return;
    }
    if (isHandlingAddToCartCall) {
      const success = await addToCartRequest({
        product: addToCartProduct,
        accessoryIds: accessoriesList?.map(x => Number(x)),
        quantity: Number(quantity),
        gtmAtcProps: {
          source: "carousel",
          carouselProps: getCarouselProps(inputRef.current)
        }
      });
      onClickCtrTrack(carouselName, originalLinkingItemNumber, product.link);
      if (!success) {
        setErrorMessage("There was an error adding this item to your cart.");
        return;
      }
      new Notification({
        imageUrl: product.imagePath || "/images/products/new/imagecomingsoon_tn.jpg",
        heading: ReactDOMServer.renderToString(<SuccessMessage quantity={qtyValue(quantity)} />),
        href: "/viewcart.cfm",
        body: product.description,
        buttonText: "View Cart",
        secondButtonText: "Checkout",
        secondHref: "/viewinfo.cfm"
      });
    } else if (addCarouselItemToCart) {
      addCarouselItemToCart(product.itemNumber, quantity, accessoriesList?.join(","));
      addToCartAnalytics({
        source: "carousel",
        carouselProps: getCarouselProps(inputRef.current)
      }, {
        itemNumber: product.itemNumber
      });
    }
  };
  const {
    openModals,
    activeModal,
    closeModal,
    submitModal
  } = useAddToCartModals({
    onComplete: (results, openModals) => {
      AddToCart({
        openModals,
        accessoriesList: results.accessories
      });
    }
  });
  const handleAddToCart = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    return AddToCart({
      openModals
    });
  };
  const isAccessoriesModalOpen = activeModal === "accessories";
  const ProductModals = () => <>
      {isAccessoriesModalOpen && <AccessoriesModal {...{
      accessories,
      isOpen: isAccessoriesModalOpen,
      itemNumber: product.itemNumber,
      closeModal,
      submitModal
    }} />}
    </>;
  const showViewDetails = product.grouping;
  const showViewReplacement = !!(product.isReplacementEligible && !showViewDetails);
  const showOutOfStock = product.outOfStock && !showViewReplacement && !showViewDetails;
  const displayRequiredCustomization = shouldDisplayRequiredCustomization(product) && !showOutOfStock && !showViewReplacement && !showViewDetails;
  const showAddToCart = !displayRequiredCustomization && !showOutOfStock && !showViewReplacement && !showViewDetails;
  if (showQuantityDropdown !== undefined && showQuantityDropdown) {
    setIsQuantityShown?.(true);
  }
  return <>
      <ProductModals data-sentry-element="ProductModals" data-sentry-source-file="CarouselAddToCart.tsx" />
      {showViewDetails && <ViewDetailsButton containsQuantityDropdown={containsQuantityDropdown} showQuantityDropdown={showQuantityDropdown} productLink={product.link} />}
      {displayRequiredCustomization && <CustomizeButton customizationUrl={String(product.customizationUrl)} itemNumber={product.itemNumber} />}
      {showOutOfStock && <NotifyMeButton emailAddress={userEmail ?? ""} itemNumber={product.itemNumber} />}
      {showViewReplacement && <ViewReplacementButton productReplacement={product.productReplacement} containsQuantityDropdown={containsQuantityDropdown} showQuantityDropdown={showQuantityDropdown} />}

      {showAddToCart && <div className="add-to-cart">
          <form action="/viewcart.cfm" className={formClassName} id={formId} method="post">
            <div className={cls("btn-container", "flex", "items-stretch", showQuantityDropdown ? "p-0" : "", showQuantityDropdown && !isMiniCarousel ? "flex-col" : "max-h-8")}>
              <div aria-live="assertive">
                {errorMessage.length > 0 && <p id={`add-to-cart-error-${product.itemNumber}`} className="messages">
                    {errorMessage}
                  </p>}
              </div>
              {showQuantityDropdown && <QuantityDropdown minBuy={minimumBuy} mustBuy={mustBuy} useSingleItemStyle={useSingleItemStyle} handleQuantityChange={handleQuantityChange} extraQuantityContainerClassName={extraQuantityContainerClassName} />}

              {!showQuantityDropdown && <div className="flex items-stretch">
                  <input autoComplete="off" type="number" className="quantity block h-full box-border static" name="qty" value={quantity} aria-label={`Quantity for ${product.description}`} onChange={handleQuantityChange} data-testid="inputQuantity" aria-describedby={`add-to-cart-error-${product.itemNumber}`} />
                </div>}
              <input className="item_number" name="item_number" type="hidden" value={product.itemNumber} />
              <input ref={inputRef} type="submit" className={`btn btn-cart btn-small static ${buttonClasses} ${showQuantityDropdown ? "rounded-l" : "rounded-l-none"}`} value="Add to Cart" onClick={handleAddToCart} name="addToCartButton" data-action="addToCart" data-testid="itemAddCart" />
            </div>
          </form>
        </div>}
    </>;
};
export default CarouselAddToCart;